import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { useLang } from '../../context/LangContext';
import CenteredLayout from '../../layout/CenteredLayout';
import ProjectAppbar from '../../customComponents/ProjectAppbar';
import PageContainerLayout from '../../layout/PageContainerLayout';
import { updatePushSettings, getUserInfo } from '../../lib/api/user/userApi';

const NotificationContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const SectionContainer = styled.div`
  background-color: #ffffff;
  padding: 24px 0;
`;

const WebSectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin-bottom: 24px;
  text-align: left;
  
  @media (max-width: 767px) {
    display: none;
  }
`;

const SettingItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0;
`;

const SettingInfo = styled.div`
  flex: 1;
`;

const SettingTitle = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: #333;
  text-align: left;
  margin-bottom: 4px;
`;

const SettingDescription = styled.div`
  font-size: 14px;
  color: #666;
  text-align: left;
  line-height: 1.4;
`;

const ToggleSwitch = styled.div<{ $isOn: boolean; $disabled?: boolean }>`
  width: 50px;
  height: 30px;
  background-color: ${props => props.$disabled ? '#F0F0F0' : props.$isOn ? '#69b5b0' : '#E5E5E5'};
  border-radius: 15px;
  position: relative;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  transition: background-color 0.3s ease;
  opacity: ${props => props.$disabled ? 0.6 : 1};
  
  &::after {
    content: '';
    position: absolute;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background-color: white;
    top: 4px;
    left: ${props => props.$isOn ? '24px' : '4px'};
    transition: left 0.3s ease;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }
`;

// 다국어 텍스트 객체
const texts = {
  ko: {
    notificationSettings: '알림설정',
    notifications: '알림',
    allNotifications: '알림 전체 설정',
    allNotificationsDesc: '경매 시작, 낙찰 등 주요 알림 권한 선택',
    auctionStart: '경매시작 알림',
    auctionStartDesc: '경매 시작 알림 권한 선택',
    auctionWinning: '경매낙찰 알림',
    auctionWinningDesc: '참여 경매 낙찰 알림 권한 선택',
    updateSuccess: '알림 설정이 성공적으로 변경되었습니다',
    updateFailed: '알림 설정 변경에 실패했습니다'
  },
  en: {
    notificationSettings: 'Notification Settings',
    notifications: 'Notifications',
    allNotifications: 'All Notifications Setting',
    allNotificationsDesc: 'Select permission for major notifications such as auction start and winning',
    auctionStart: 'Auction Start Alert',
    auctionStartDesc: 'Select auction start notification permission',
    auctionWinning: 'Auction Winning Alert',
    auctionWinningDesc: 'Select participating auction winning notification permission',
    updateSuccess: 'Notification settings have been successfully updated',
    updateFailed: 'Failed to update notification settings'
  },
};

function NotificationSettings() {
  const navigate = useNavigate();
  const { lang } = useLang();

  const t = texts[lang];
  
  // 개별 설정 상태
  const [auctionStartEnabled, setAuctionStartEnabled] = useState(true);
  const [auctionWinningEnabled, setAuctionWinningEnabled] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  
  // 전체 설정 상태는 개별 설정들을 기반으로 계산
  const allNotificationsEnabled = auctionStartEnabled && auctionWinningEnabled;

  // 컴포넌트 마운트 시 유저 정보 가져오기
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const response = await getUserInfo();
        if (response.data.statusCode === 200 && response.data.data) {
          const userData = response.data.data;
          // API 응답의 오타를 고려하여 두 가지 경우 모두 체크
          setAuctionStartEnabled(userData.receiveAutctionStartPush || userData.receiveAuctionStartPush || false);
          setAuctionWinningEnabled(userData.recieveAuctionWonPush || userData.receiveAuctionWonPush || false);
        }
      } catch (error) {
        console.error('유저 정보 가져오기 실패:', error);
        // 에러 발생 시 기본값 유지
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserInfo();
  }, []);

  // API 호출 함수
  const updateNotificationSettings = async (receiveAuctionStartPush: boolean, receiveAuctionWonPush: boolean) => {
    try {
      const response = await updatePushSettings({
        receiveAuctionStartPush: receiveAuctionStartPush,  // 서버 필드명에 맞춤 (오타 포함)
        recieveAuctionWonPush: receiveAuctionWonPush        // 서버 필드명에 맞춤 (오타 포함)
      });
      console.log('푸쉬 설정 업데이트 성공:', response.data);
      toast.success(t.updateSuccess);
    } catch (error) {
      console.error('푸쉬 설정 업데이트 실패:', error);
      toast.error(t.updateFailed);
      // 에러를 다시 throw하여 호출하는 곳에서 처리할 수 있도록 함
      throw error;
    }
  };

  // 전체 설정 토글
  const toggleAllNotifications = async () => {
    const newValue = !allNotificationsEnabled;
    const prevAuctionStart = auctionStartEnabled;
    const prevAuctionWinning = auctionWinningEnabled;
    
    // 먼저 UI 상태 변경
    setAuctionStartEnabled(newValue);
    setAuctionWinningEnabled(newValue);
    
    try {
      await updateNotificationSettings(newValue, newValue);
    } catch (error) {
      // 에러 발생 시 이전 상태로 롤백
      setAuctionStartEnabled(prevAuctionStart);
      setAuctionWinningEnabled(prevAuctionWinning);
    }
  };

  // 경매 시작 알림 토글
  const toggleAuctionStart = async () => {
    const newValue = !auctionStartEnabled;
    const prevValue = auctionStartEnabled;
    
    // 먼저 UI 상태 변경
    setAuctionStartEnabled(newValue);
    
    try {
      await updateNotificationSettings(newValue, auctionWinningEnabled);
    } catch (error) {
      // 에러 발생 시 이전 상태로 롤백
      setAuctionStartEnabled(prevValue);
    }
  };

  // 경매 낙찰 알림 토글
  const toggleAuctionWinning = async () => {
    const newValue = !auctionWinningEnabled;
    const prevValue = auctionWinningEnabled;
    
    // 먼저 UI 상태 변경
    setAuctionWinningEnabled(newValue);
    
    try {
      await updateNotificationSettings(auctionStartEnabled, newValue);
    } catch (error) {
      // 에러 발생 시 이전 상태로 롤백
      setAuctionWinningEnabled(prevValue);
    }
  };

  const settings = [
    {
      id: 'all-notifications',
      title: t.allNotifications,
      description: t.allNotificationsDesc,
      enabled: allNotificationsEnabled,
      onToggle: toggleAllNotifications
    },
    {
      id: 'auction-start',
      title: t.auctionStart,
      description: t.auctionStartDesc,
      enabled: auctionStartEnabled,
      onToggle: toggleAuctionStart
    },
    {
      id: 'auction-winning',
      title: t.auctionWinning,
      description: t.auctionWinningDesc,
      enabled: auctionWinningEnabled,
      onToggle: toggleAuctionWinning
    }
  ];

  return (
    // <MainLayout>
      <CenteredLayout>
        <ProjectAppbar 
          currentPage="mypage"
          title={t.notificationSettings}
          showBackButton={true}
          onBack={() => navigate('/mypage')}
        />
        <PageContainerLayout>
        <NotificationContainer>
          <SectionContainer>
            <WebSectionTitle>{t.notifications}</WebSectionTitle>
            {settings.map((setting) => (
              <SettingItem key={setting.id}>
                <SettingInfo>
                  <SettingTitle>{setting.title}</SettingTitle>
                  <SettingDescription>{setting.description}</SettingDescription>
                </SettingInfo>
                <ToggleSwitch 
                  $isOn={setting.enabled}
                  $disabled={isLoading}
                  onClick={isLoading ? undefined : setting.onToggle}
                />
              </SettingItem>
            ))}
          </SectionContainer>
        </NotificationContainer>
        </PageContainerLayout>
      </CenteredLayout>
    // </MainLayout>
  );
}

export default NotificationSettings;
